import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import mergeClasses from '../../../utils/shallowMerge';

import defaultClasses from '../../organisms/cartPage/cartItem/CartItem.module.scss';
import { useWishlistContext } from '@srcDS/provider/wishlist/WishlistContext';
import { useLayoutContext } from '@srcDS/provider/layout/LayoutContext';
import { RootState, WishlistContextType, LinkToWishlistProps, WishlistItem } from './types/LinkToWishlistProps';
import Button from '../../atoms/button';
import IconButton from '@srcDS/components/atoms/iconButton/IconButton';
import PrimaryLoader from '@srcDS/components/atoms/primaryLoader/PrimaryLoader';
import { HeartEmpty, HeartFilled } from '@srcDS/components/atoms/icons';

const LinkToWishlist: React.FC<LinkToWishlistProps> = ({
  item = {},
  lineItemId = '',
  firstSkuId,
  toSignUp = () => {},
  setLoading = () => {},
  placement,
  loading = false,
  isIcon = false,
  customClasses = '',
  buttonText = 'Add to Wishlist',
}) => {
  const styles = mergeClasses(defaultClasses, customClasses);
  const { addToWishlist, wishlist, setNextWishlistAction, removeFromWishlist, skusInWishlist, isLoadingWishlist } =
    useWishlistContext() as WishlistContextType;

  const { setShowSearchDropdown } = useLayoutContext();
  const isLoggedIn = useSelector((state: RootState) => state.app?.context?.session?.loggedIn || false);

  const wishlistItem = wishlist?.lineItems?.find((el) => el.variant?.sku === item.variant?.sku);
  const isActive = skusInWishlist && firstSkuId ? skusInWishlist.includes(firstSkuId) : false;

  const handleWishlistOperation = async (itemToModify: WishlistItem): Promise<void> => {
    setLoading(true);

    try {
      if (isActive && wishlistItem) {
        await removeFromWishlist(
          wishlistItem.lineItemId,
          () => setLoading(false),
          () => setLoading(false),
        );
      } else if (itemToModify.variant) {
        await addToWishlist(
          {
            ...itemToModify,
            productName: itemToModify?.name || itemToModify?.productName,
          },
          itemToModify.variant,
          1,
          () => setLoading(false),
          () => setLoading(false),
          placement,
        );
      }
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  /**
   * Sets up wishlist action to be performed after user signs in
   */
  const handlePreSignup = useCallback(() => {
    setNextWishlistAction({
      action: 'addAfterSigning',
      method: handleWishlistOperation,
      item,
      lineItemId,
    });

    if (setShowSearchDropdown) {
      setShowSearchDropdown(false);
    }

    if (toSignUp) {
      toSignUp();
    }
  }, [setNextWishlistAction, item, lineItemId, setShowSearchDropdown, toSignUp]);

  const handleWishlistClick = (e: React.MouseEvent): void => {
    e.stopPropagation();

    if (isLoadingWishlist) {
      return;
    }

    if (!isLoggedIn) {
      handlePreSignup();
    } else {
      handleWishlistOperation(item);
    }
  };

  // Don't render text button if item is already in wishlist
  if (wishlistItem && !isIcon) {
    return null;
  }

  if (isIcon) {
    return (
      <div className={styles.linkToWishlist} data-wishlist-button="wishlist_icon_button" onClick={handleWishlistClick}>
        <IconButton
          // @ts-ignore
          color="primary"
          iconRegular={() => <HeartEmpty width="20" height="20" />}
          iconHover={() => <HeartFilled width="20" height="20" />}
          iconActive={() => <HeartFilled width="20" height="20" />}
          iconLoading={() => <PrimaryLoader mode="segment" size={16} />}
          loading={loading}
          isActive={isActive}
          ariaLabel="Add to Wishlist"
        />
      </div>
    );
  }

  return (
    // @ts-ignore
    <Button
      variant="primary"
      as="text"
      className={styles.linkToWishlist}
      data-wishlist-button="wishlist_button"
      ariaLabel="wishlist_button"
      onClick={handleWishlistClick}
    >
      {buttonText}
    </Button>
  );
};

export default LinkToWishlist;
